/**
 * Scene links between objects and bodies: every ActivatorBase reference the game supports
 * (single `activator` fields, `activators` lists, moving platform activators, control panels).
 */
import type { Draft } from 'immer';
import type { LevelDoc } from '../model/format.ts';
import { findObject, useEditor, type Selection } from './store.ts';
import { linkFields, SCHEMAS } from './schema.ts';
import { movingOf } from './scene.ts';
import { bodyComps, bodyCompTypes, compData, fieldAt, objectComps, ownerRef, refOf, refsOf, writeField, writeList, type CompAddr } from './components.ts';

/** MonoBehaviours deriving from ActivatorBase (can drive moving platforms, lasers, modifiers, ...). */
export const ACTIVATOR_TYPES = [
  'SwitchHandler', 'ZoneActivator', 'TimedActivator', 'ActivatorModifier', 'PlayerActionActivator', 'TouchBlock', 'ColorMatchBlock',
  'DashPlatform', 'DashActivator', 'Checkpoint', 'Coin', 'Gem', 'GemShard', 'ReDash', 'ForceDash', 'PowerupCollectable',
  'BodyControlHandler', 'BodyControlPanel', 'Door', 'PlatformMovePoint',
];

export type Accept = 'activator' | 'panel' | 'body';

export interface LinkSlot {
  addr: CompAddr;
  /** serialized field name of the reference (list or single) */
  path: string;
  list: boolean;
  accept: Accept;
  label: string;
}

function entityTypes(doc: LevelDoc, ent: string): string[] {
  const [kind, id] = ent.split(':');
  if (kind === 'obj') {
    const f = findObject(doc, id);
    return f ? objectComps(doc, f.obj).map((c) => c.type) : [];
  }
  if (kind === 'body') {
    const b = doc.bodies.find((x) => x.id === id);
    return b ? bodyCompTypes(b) : [];
  }
  return [];
}

/** Ref to link an entity ("obj:id" / "body:id") as a target of the given kind, e.g. "obj:o12#SwitchHandler". */
export function targetRefFor(doc: LevelDoc, ent: string, accept: Accept): string | null {
  if (accept === 'body') return ent.startsWith('body:') ? ent : null;
  const types = entityTypes(doc, ent);
  const wanted = accept === 'panel' ? ['BodyControlPanel'] : ACTIVATOR_TYPES;
  for (const t of wanted) if (types.includes(t)) return `${ent}#${t}`;
  return null;
}

export function activatorRefFor(doc: LevelDoc, objId: string): string | null {
  return targetRefFor(doc, `obj:${objId}`, 'activator');
}

/** All linkable reference fields of an object or body. */
export function slotsOf(doc: LevelDoc, ent: string): LinkSlot[] {
  const [kind, id] = ent.split(':');
  const out: LinkSlot[] = [];
  const fromComps = (comps: { addr: CompAddr; type: string }[]) => {
    for (const c of comps) {
      for (const f of linkFields(c.type)) {
        const list = f.kind.k !== 'activator';
        out.push({ addr: c.addr, path: f.p, list, accept: f.kind.k === 'panels' ? 'panel' : 'activator', label: `${SCHEMAS[c.type].title}: ${f.label}` });
      }
      if (c.type === 'BodyControlHandler') out.push(controlledSlot(c.addr));
    }
  };
  if (kind === 'obj') {
    const f = findObject(doc, id);
    if (f) fromComps(objectComps(doc, f.obj));
  } else if (kind === 'body') {
    const b = doc.bodies.find((x) => x.id === id);
    if (b) {
      if (movingOf(b)) out.push({ addr: { kind: 'moving', body: id }, path: 'activators', list: true, accept: 'activator', label: 'Moving platform: Activators' });
      fromComps(bodyComps(b));
    }
  }
  return out;
}

/** The slot the Link tool fills by default for a selection (first list, else first single field). */
export function primarySlot(doc: LevelDoc, ent: string): LinkSlot | null {
  const slots = slotsOf(doc, ent).filter((s) => s.accept === 'activator');
  return slots.find((s) => s.list) ?? slots[0] ?? null;
}

export function selectionEntity(sel: Selection): string | null {
  if (sel.kind === 'body') return `body:${sel.id}`;
  if (sel.kind === 'objects' && sel.ids.length === 1) return `obj:${sel.ids[0]}`;
  return null;
}

export function controlledSlot(addr: CompAddr): LinkSlot {
  return { addr, path: 'uiRigidBodiesOrGameObjects', list: true, accept: 'body', label: 'Body controller: Controlled bodies' };
}

export function slotRefs(doc: LevelDoc, slot: LinkSlot): string[] {
  const data = compData(doc, slot.addr);
  if (!data) return [];
  const v = fieldAt(data, slot.path);
  if (slot.accept === 'body') return (Array.isArray(v) ? v : []).map((it: any) => refOf(it?.rigidbodyOrParent) ?? '');
  return slot.list ? refsOf(v) : [refOf(v)].filter((r): r is string => !!r);
}

/** Rewrite a BodyControlHandler's controlled bodies (RigidbodyMetadata list). */
export function writeControlled(d: Draft<LevelDoc>, addr: CompAddr, items: any[]) {
  writeList(
    d,
    addr,
    'uiRigidBodiesOrGameObjects',
    items.map((it) => {
      const r = refOf(it?.rigidbodyOrParent);
      const rec: Record<string, string | { ref: string }> = {
        xAxisControls: String(it?.xAxisControls ?? '0'),
        xInputAxisScale: String(it?.xInputAxisScale ?? '1'),
        yAxisControls: String(it?.yAxisControls ?? '0'),
        yInputAxisScale: String(it?.yInputAxisScale ?? '1'),
      };
      if (r) rec.rigidbodyOrParent = { ref: r };
      return rec;
    }),
  );
}

const base = (r: string) => r.split('#')[0];

/** Link (or unlink, when already linked) a target ref into a slot. Returns the new linked state. */
export function toggleLink(slot: LinkSlot, ref: string): boolean {
  const st = useEditor.getState();
  const doc = st.doc!;
  const cur = slotRefs(doc, slot);
  const has = cur.some((r) => base(r) === base(ref));
  st.change(has ? 'Unlink' : 'Link', (d) => {
    if (slot.accept === 'body') {
      const items: any[] = (fieldAt(compData(doc, slot.addr), slot.path) as any[]) ?? [];
      const next = has
        ? items.filter((it) => base(refOf(it?.rigidbodyOrParent) ?? '') !== base(ref))
        : [...items, { rigidbodyOrParent: { ref }, xAxisControls: '1', xInputAxisScale: '1', yAxisControls: '0', yInputAxisScale: '1' }];
      writeControlled(d, slot.addr, next);
    } else if (slot.list) {
      const next = has ? cur.filter((r) => base(r) !== base(ref)) : [...cur, ref];
      writeList(d, slot.addr, slot.path, next.map((r) => ({ ref: r })));
    } else writeField(d, slot.addr, slot.path, { r: has ? '' : ref });
  });
  return !has;
}

export function setSingleLink(slot: LinkSlot, ref: string | null) {
  useEditor.getState().change(ref ? 'Link' : 'Unlink', (d) => writeField(d, slot.addr, slot.path, { r: ref ?? '' }));
}

export interface Link {
  /** owner entity ("obj:id" / "body:id") */
  from: string;
  /** linked entity ref (may carry a #Type) */
  to: string;
  kind: 'activator' | 'panel' | 'controlled';
}

const linkCache = new WeakMap<LevelDoc, Link[]>();

/** Every link in the level except moving platform activators (drawn with the platform path). */
export function allLinks(doc: LevelDoc): Link[] {
  const cached = linkCache.get(doc);
  if (cached) return cached;
  const out: Link[] = [];
  const scan = (from: string, comps: { type: string; data: any }[]) => {
    for (const c of comps) {
      if (!c.data) continue;
      for (const f of linkFields(c.type)) {
        const v = c.data[f.p];
        const refs = f.kind.k === 'activator' ? [refOf(v)].filter((r): r is string => !!r) : refsOf(v);
        for (const to of refs) out.push({ from, to, kind: f.kind.k === 'panels' ? 'panel' : 'activator' });
      }
      if (c.type === 'BodyControlHandler') {
        for (const it of Array.isArray(c.data.uiRigidBodiesOrGameObjects) ? c.data.uiRigidBodiesOrGameObjects : []) {
          const to = refOf(it?.rigidbodyOrParent);
          if (to) out.push({ from, to, kind: 'controlled' });
        }
      }
    }
  };
  for (const o of [...doc.objects, ...doc.bodies.flatMap((b) => b.objects)]) scan(`obj:${o.id}`, objectComps(doc, o));
  for (const b of doc.bodies) scan(`body:${b.id}`, bodyComps(b));
  linkCache.set(doc, out);
  return out;
}

/** What an entity drives: links pointing at it, including moving platforms. */
export function linksTo(doc: LevelDoc, ent: string): { from: string; label: string }[] {
  const out: { from: string; label: string }[] = [];
  for (const l of allLinks(doc)) if (base(l.to) === ent) out.push({ from: l.from, label: l.kind === 'controlled' ? 'controls it' : l.kind === 'panel' ? 'uses it as a panel' : 'uses it' });
  for (const b of doc.bodies) if (movingOf(b)?.activators.some((a) => base(a) === ent)) out.push({ from: `body:${b.id}`, label: 'moves with it' });
  return out;
}

/** BodyControlHandlers (on objects or bodies) steering a body. */
export function controllersOf(doc: LevelDoc, bodyId: string): { addr: CompAddr; owner: string; index: number; item: any }[] {
  const out: { addr: CompAddr; owner: string; index: number; item: any }[] = [];
  const scan = (comps: { addr: CompAddr; type: string; data: any }[]) => {
    for (const c of comps) {
      if (c.type !== 'BodyControlHandler' || !Array.isArray(c.data?.uiRigidBodiesOrGameObjects)) continue;
      c.data.uiRigidBodiesOrGameObjects.forEach((it: any, index: number) => {
        const r = refOf(it?.rigidbodyOrParent);
        if (r && base(r) === `body:${bodyId}`) out.push({ addr: c.addr, owner: ownerRef(c.addr), index, item: it });
      });
    }
  };
  for (const o of [...doc.objects, ...doc.bodies.flatMap((b) => b.objects)]) scan(objectComps(doc, o));
  for (const b of doc.bodies) scan(bodyComps(b));
  return out;
}

/** Display name of an entity ref ("obj:id#Type" -> "Switch (2)"). */
export function entityNamer(doc: LevelDoc) {
  return (ref: string) => {
    const [ent, type] = ref.split('#');
    const [kind, id] = ent.split(':');
    let n = ref;
    if (kind === 'obj') n = findObject(doc, id)?.obj.name ?? `missing ${id}`;
    else if (kind === 'body') n = doc.bodies.find((b) => b.id === id)?.name ?? 'missing body';
    else if (kind === 'layer') n = [...doc.layers, ...doc.bodies.flatMap((b) => b.layers)].find((l) => l.id === id)?.name ?? 'layer';
    return type && !SCHEMAS[type] ? `${n} (${type})` : n;
  };
}
